import { Navigate, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Newspaper, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CardContent, CardFooter, CardIcon } from '@/components/ui/card'
import { useCurrentUser } from '@/contexts/UserContext'
import { usePersistedState } from '@/hooks/usePersistedState'
import {
  isReleaseNotesAdmin,
  ReleaseNotesApi,
  type ReleaseNoteState,
} from '@/services/releaseNotes/releaseNotesApi'
import { AppShell } from '../../components/AppShell'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { ListTabs } from '../../components/ListTabs'
import { ReleaseNoteTable } from '../../components/ReleaseNoteTable'

export function ReleaseNotesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentUser = useCurrentUser()
  const isAdmin = isReleaseNotesAdmin(currentUser?.username)
  const [state, setState] = usePersistedState<ReleaseNoteState>(
    'filters.releaseNotes.state',
    'ACTIVE'
  )

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ['release-notes', state],
    queryFn: () => ReleaseNotesApi.getAll(state),
    enabled: isAdmin,
  })

  // The backend serves the full list to its own admin accounts only.
  if (currentUser && !isAdmin) return <Navigate to="/select-module" replace />

  return (
    <AppShell crumbs={[{ label: t('releaseNotes.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('releaseNotes.title')}</h1>
            <p>{t('releaseNotes.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button
              onClick={() => navigate(`/super-admin/release-notes/${crypto.randomUUID()}?new=true`)}
            >
              <Plus className="size-4" /> {t('releaseNotes.newReleaseNote')}
            </Button>
          </div>
        </div>

        <ListCard>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Newspaper} />
                {t('releaseNotes.title')}
              </>
            }
            count={notes.length}
            controls={
              <ListTabs
                tabs={[
                  { key: 'ACTIVE', label: t('releaseNotes.states.ACTIVE') },
                  { key: 'ARCHIVED', label: t('releaseNotes.states.ARCHIVED') },
                ]}
                value={state}
                onValueChange={setState}
              />
            }
          />

          <CardContent className="p-0">
            <ReleaseNoteTable notes={notes} loading={isLoading} />
          </CardContent>

          {notes.length > 0 && (
            <CardFooter>
              <span className="text-xs text-(--ad-text-muted)">
                {t('releaseNotes.releaseNoteCount', { count: notes.length })}
              </span>
            </CardFooter>
          )}
        </ListCard>
      </div>
    </AppShell>
  )
}
